'use client';

/**
 * NotificationGroupCard Component
 *
 * Groups multiple related notifications into a single expandable card.
 * Prevents notification fatigue by batching similar items.
 * Apple Liquid Glass-inspired collapsible card with count badge.
 *
 * @see Issue #699 - Notification System Fidelity
 * @see Apple WWDC 2025 - Liquid Glass design language
 */

import MarkAllReadIcon from '@mui/icons-material/DoneAll';
import CollapseIcon from '@mui/icons-material/ExpandLess';
import ExpandIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import { alpha, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { AnimatePresence, motion } from 'framer-motion';
import React, { forwardRef, useCallback, useState } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { baseColors } from '../../theme/palette';
import { springPresets } from '../../theme/transitions';
import { iconSizes } from '../../theme/typography';
import { GlassIconButton } from '../GlassIconButton';
import type { NotificationItemData } from '../NotificationItem';
import { NotificationItem } from '../NotificationItem';

// ============================================================================
// Types
// ============================================================================

export interface NotificationGroupCardProps {
  /** Group title (e.g., "Document Updates", "Case Activity") */
  title: string;

  /** Icon element for the group */
  icon?: React.ReactElement;

  /** Accent color for the group */
  accentColor?: string;

  /** Notifications in this group */
  notifications: NotificationItemData[];

  /** Whether the group starts expanded */
  defaultExpanded?: boolean;

  /** Maximum items shown when collapsed (shows a summary) */
  collapsedPreview?: number;

  /** Called when "mark all in group as read" */
  onMarkGroupRead?: (notifications: NotificationItemData[]) => void;

  /** Called when a notification is clicked */
  onNotificationClick?: (notification: NotificationItemData) => void;

  /** Called when toggle read is triggered */
  onToggleRead?: (notification: NotificationItemData) => void;
}

// ============================================================================
// Component
// ============================================================================

export const NotificationGroupCard = forwardRef<HTMLDivElement, NotificationGroupCardProps>(
  (
    {
      title,
      icon,
      accentColor = baseColors.primary[500],
      notifications,
      defaultExpanded = false,
      collapsedPreview = 1,
      onMarkGroupRead,
      onNotificationClick,
      onToggleRead,
    },
    ref,
  ) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const { t } = useI18n();
    const [expanded, setExpanded] = useState(defaultExpanded);

    const unreadCount = notifications.filter((n) => !n.isRead).length;
    const hasUnread = unreadCount > 0;
    const previewItems = notifications.slice(0, collapsedPreview);
    const hiddenCount = notifications.length - collapsedPreview;

    const toggleExpanded = useCallback(() => {
      setExpanded((prev) => !prev);
    }, []);

    const handleMarkGroupRead = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        onMarkGroupRead?.(notifications);
      },
      [onMarkGroupRead, notifications],
    );

    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={springPresets.snappy}
        layout
      >
        <Box
          sx={{
            borderRadius: 2.5,
            overflow: 'hidden',
            // Liquid Glass card surface
            background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.55)',
            backdropFilter: 'blur(20px) saturate(150%)',
            WebkitBackdropFilter: 'blur(20px) saturate(150%)',
            border: `0.5px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.65)'}`,
            boxShadow: isDark
              ? '0 2px 12px rgba(0,0,0,0.25), inset 0 0.5px 0 rgba(255,255,255,0.04)'
              : '0 2px 12px rgba(0,0,0,0.04), inset 0 0.5px 0 rgba(255,255,255,0.6)',
            transition: 'all 0.2s ease',
          }}
        >
          {/* Group header */}
          <Box
            onClick={toggleExpanded}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggleExpanded();
              }
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2,
              py: 1.5,
              cursor: 'pointer',
              '&:hover': {
                bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.015)',
              },
            }}
          >
            {/* Icon */}
            {icon && (
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha(accentColor, isDark ? 0.15 : 0.1),
                  color: accentColor,
                  '& .MuiSvgIcon-root': { fontSize: iconSizes.sm },
                }}
              >
                {icon}
              </Box>
            )}

            {/* Title + count */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  fontSize: '0.82rem',
                }}
              >
                {title}
              </Typography>
            </Box>

            {/* Unread badge */}
            {hasUnread && (
              <Box
                sx={{
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  bgcolor: alpha(accentColor, isDark ? 0.2 : 0.12),
                  color: accentColor,
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  {unreadCount}
                </Typography>
              </Box>
            )}

            {/* Mark group read */}
            {hasUnread && onMarkGroupRead && (
              <GlassIconButton
                size="xs"
                variant="standard"
                tooltip={t('notification.group.markAllRead')}
                onClick={handleMarkGroupRead}
              >
                <MarkAllReadIcon sx={{ fontSize: iconSizes.xs }} />
              </GlassIconButton>
            )}

            {/* Expand/collapse */}
            {notifications.length > collapsedPreview && (
              <motion.div animate={{ rotate: expanded ? 180 : 0 }} transition={springPresets.snappy}>
                {expanded ? (
                  <CollapseIcon sx={{ fontSize: iconSizes.md, color: 'text.disabled' }} />
                ) : (
                  <ExpandIcon sx={{ fontSize: iconSizes.md, color: 'text.disabled' }} />
                )}
              </motion.div>
            )}
          </Box>

          {/* Preview (when collapsed) */}
          {!expanded && (
            <Box>
              {previewItems.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onClick={onNotificationClick}
                  onToggleRead={onToggleRead}
                  compact
                />
              ))}
              {hiddenCount > 0 && (
                <Box
                  onClick={toggleExpanded}
                  sx={{
                    px: 2,
                    py: 1,
                    textAlign: 'center',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)' },
                  }}
                >
                  <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 500 }}>
                    {t('notification.group.expand')} (+{hiddenCount})
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Expanded list */}
          <Collapse in={expanded} unmountOnExit>
            <AnimatePresence mode="popLayout">
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onClick={onNotificationClick}
                  onToggleRead={onToggleRead}
                  compact
                />
              ))}
            </AnimatePresence>
          </Collapse>
        </Box>
      </motion.div>
    );
  },
);

NotificationGroupCard.displayName = 'NotificationGroupCard';

export default NotificationGroupCard;
